import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Eye, Palette } from "lucide-react";

const RecolourPreview = ({
  originalUrl,
  recolouredUrl,
  loading,
  progressMessage,
  timedOut,
  onRetry,
}) => {
  const previewRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showOriginal, setShowOriginal] = useState(true);
  const originalImgRef = useRef(null);
  const [imageHeight, setImageHeight] = useState(null);

  // Update mobile status on window resize.
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset height when a new image is selected
  useEffect(() => {
    setImageHeight(null);
  }, [originalUrl]); // or whatever prop changes when new image is loaded

  useEffect(() => {
    if (originalUrl && originalImgRef.current && imageHeight === null) {
      setImageHeight(originalImgRef.current.clientHeight);
    }
  }, [originalUrl, showOriginal, imageHeight]);

  // Update height once the new image loads
  const handleImageLoad = () => {
    if (originalImgRef.current) {
      setImageHeight(originalImgRef.current.clientHeight);
    }
  };

  useEffect(() => {
    if (!loading && recolouredUrl) {
      previewRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [recolouredUrl, loading]);

  useEffect(() => {
    return () => {
      if (originalUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(originalUrl);
      }
      if (recolouredUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(recolouredUrl);
      }
    };
  }, []);

  useEffect(() => {
    if (
      showOriginal &&
      originalUrl &&
      originalImgRef.current &&
      originalImgRef.current.complete
    ) {
      setImageHeight(originalImgRef.current.clientHeight);
    }
  }, [showOriginal, originalUrl]);

  const renderImage = useCallback(
    (url, label) => {
      const imageContainer = (content) => (
        <div
          className="rounded-3xl border border-vedicGold shadow-innerGlow bg-deepSlate w-full max-w-[500px] flex justify-center items-center overflow-hidden"
          style={{ height: imageHeight || 400 }}
        >
          {content}
        </div>
      );

      const labelStyle =
        "text-lg font-semibold text-marbleWhite mb-2 font-serif tracking-wide";

      if (label.includes("Original")) {
        return (
          <div className="flex flex-col items-center h-full space-y-2">
            <h2 className={labelStyle}>{label}</h2>
            {url
              ? imageContainer(
                  <img
                    key={`original-${showOriginal}`} // Force remount
                    ref={originalImgRef}
                    src={url}
                    onLoad={handleImageLoad}
                    alt={label}
                    className="w-full object-contain max-w-[500px]"
                  />
                )
              : imageContainer(
                  <p className="text-marbleWhite text-sm italic opacity-60 px-4 py-12 text-center">
                    Original image will appear here
                  </p>
                )}
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center h-full space-y-2">
          <h2 className={labelStyle}>{label}</h2>
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-vedicGold border-t-transparent rounded-full animate-spin"></div>
              <p className="text-marbleWhite text-sm">{progressMessage}</p>
              {timedOut && (
                <button
                  onClick={onRetry}
                  className="mt-2 px-3 py-1 bg-rose-600 text-white rounded-full hover:bg-rose-700 transition"
                >
                  Retry
                </button>
              )}
            </div>
          ) : url ? (
            imageContainer(
              <img
                src={url}
                alt={label}
                className="w-full object-contain max-w-[500px]"
              />
            )
          ) : (
            imageContainer(
              <p className="text-marbleWhite text-sm italic opacity-60 px-4 py-12 text-center">
                Recoloured image will appear here
              </p>
            )
          )}
        </div>
      );
    },
    [imageHeight, loading, recolouredUrl, onRetry, progressMessage, timedOut, showOriginal]
  );

  return (
    <div className="mt-6" ref={previewRef}>
      {isMobile ? (
        // Mobile view: show toggle buttons to switch between original and recoloured.
        <div className="relative">
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 flex gap-2 bg-deepSlate border border-vedicGold shadow-innerGlow rounded-full px-3 py-2">
            <motion.button
              whileTap={{ scale: 0.94 }}
              onClick={() => setShowOriginal(true)}
              className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                showOriginal
                  ? "bg-vedicGold text-deepSlate shadow"
                  : "bg-transparent text-marbleWhite hover:text-vedicGold"
              }`}
            >
              <Eye size={16} />
              Original
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.94 }}
              onClick={() => setShowOriginal(false)}
              className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                !showOriginal
                  ? "bg-vedicGold text-deepSlate shadow"
                  : "bg-transparent text-marbleWhite hover:text-vedicGold"
              }`}
            >
              <Palette size={16} />
              Recoloured
            </motion.button>
          </div>

          {showOriginal
            ? renderImage(originalUrl, "Original Image:")
            : renderImage(recolouredUrl, "Recoloured Image:")}
        </div>
      ) : (
        // Desktop view: render the two images sequentially, one after the other.
        <div className="flex flex-row gap-4 items-start justify-center">
          <div className="w-1/2">
            {renderImage(originalUrl, "Original Image:")}
          </div>
          <div className="w-1/2">
            {renderImage(recolouredUrl, "Recoloured Image:")}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecolourPreview;
